import React, { useState, useEffect } from "react";
import {
  Divider,
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Appointments = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [date] = useState(getTodayDate());

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAppointments, setTotalAppointments] = useState(0);

  const {
    data = { results: [], count: 0 },
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchObjects(
    ["prescriptions", date, page, rowsPerPage],
    `prescriptions/?date=${date}&page=${page}&page_size=${rowsPerPage}`,
    token,
  );

  useEffect(() => {
    refetch();
  }, [date, page, rowsPerPage, refetch]);

  useEffect(() => {
    setTotalAppointments(data.count);
  }, [data.count]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "0px 0px 0px" }}>
        <Grid container padding={"20px"} direction="column">
          <Grid container justifyContent={"space-between"} alignItems="center">
            <Typography variant="h5">{t("pharmacy.todayCustomer")}</Typography>
          </Grid>
          <Divider sx={{ mt: 1, width: "100%" }} />

          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={4}
            >
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Box color="error.main" mt={4} textAlign="center">
              {t("pharmacy.errorLoading")}
            </Box>
          ) : (
            <>
              <TableContainer sx={{ mt: 4 }}>
                <Table
                  sx={{ minWidth: "100%" }}
                  aria-label="appointments table"
                >
                  <TableHead style={{ backgroundColor: "#f2f2f2" }}>
                    <TableRow>
                      <TableCell align="center">{t("pharmacy.time")}</TableCell>
                      <TableCell align="center">{t("pharmacy.date")}</TableCell>
                      <TableCell align="center">
                        {t("pharmacy.patientName")}
                      </TableCell>
                      <TableCell align="center">
                        {t("pharmacy.patientAge")}
                      </TableCell>
                      <TableCell align="center">
                        {t("pharmacy.doctor")}
                      </TableCell>
                      <TableCell align="center">
                        {t("pharmacy.price")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.results.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t("pharmacy.noAppointments")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.results.map((appointment) => (
                        <TableRow
                          key={appointment.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center" component="th" scope="row">
                            {appointment.time}
                          </TableCell>
                          <TableCell align="center">
                            {appointment.date}
                          </TableCell>
                          <TableCell align="center" component="th">
                            {appointment.patient_display}
                          </TableCell>
                          <TableCell align="center">
                            {appointment.patient_age_display}
                          </TableCell>
                          <TableCell align="center">
                            {appointment.doctor_display}
                          </TableCell>
                          <TableCell align="center">
                            {appointment.total_price}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={4}
                mb={2}
                flexWrap="wrap"
                gap={2}
              >
                {/* Rows Per Page Selector */}
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" mr={1}>
                    {t("pharmacy.rowsPerPage")}:
                  </Typography>
                  <FormControl variant="standard" sx={{ minWidth: 80 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      label={t("pharmacy.rowsPerPage")}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Pagination Component */}
                <Pagination
                  count={Math.ceil(totalAppointments / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default Appointments;
